import React, { useState, useEffect, useRef } from 'react';
import styled from 'styled-components';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTimes, faComment } from '@fortawesome/free-solid-svg-icons';
import axios from 'axios';
import { v4 as uuidv4 } from 'uuid';

const ChatBox = styled.div`
  position: fixed;
  bottom: 16px;
  right: 16px;
  width: 350px;
  height: ${({ $visibilitystate }) => ($visibilitystate === "true" ? '600px' : '0px')};
  background-color: #ffffff;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
  border-radius: 8px;
  transition: height 0.3s ease-out, box-shadow 0.2s ease-in-out;
  overflow: hidden;
  z-index: 2001;
`;

const AltLogo = styled.img`
  height: 45px;
  width: 45px;
  z-index: 2002;
  `;

const ToggleButton = styled.button`
  position: fixed;
  bottom: 90px;
  right: 20px;
  width: 50px;
  height: 50px;
  border-radius: 50%;
  background: linear-gradient(145deg, #6e9a3b, #8fbf47);
  box-shadow: 0 5px 15px rgba(0, 0, 0, 0.2);
  display: flex;
  justify-content: center;
  align-items: center;
  cursor: pointer;
  z-index: 2000;
  border: none;
  outline: none;
  transition: transform 0.1s ease-in-out;

  &:hover {
    transform: scale(1.1);
  }

  &:active {
    transform: scale(0.95);
  }

  svg {
    color: white;
  }
`;


const ChatHeader = styled.header`
  display: flex;
  justify-content: space-between;
  padding: 10px;
  background-color: #f0f0f0;
  border-bottom: 1px solid #e0e0e0;
`;

const Message = styled.p`
  padding: 10px;
  color: ${({ type }) => (type === 'user' ? '#155724' : '#2b2b2b')};
  background-color: ${({ type }) => (type === 'user' ? '#d4edda' : '#dddddd')};
  border-radius: 5px;
  margin: 10px;
`;

const ChatForm = styled.form`
  display: flex;
  padding: 10px;
  background-color: #f0f0f0;
  position: absolute;
  bottom: 0;
  width: calc(100% - 20px);

  @media (max-width: 600px) {
    padding: 5px;
  }
`;

const Input = styled.input`
  flex-grow: 1;
  overflow-y: auto;
  padding: 10px;
  border-radius: 5px;
  border: 1px solid #ccc;
  margin-right: 10px;
  min-width: 200px;
  max-width: 250px;
  margin-bottom: 0 !important;
`;

const MessageContainer = styled.div`
  padding: 10px;
  overflow-y: auto;
  height: calc(100% - 120px);
  display: flex;
  flex-direction: column;
`;

const SendButton = styled.button`
  padding: 10px;
  background-color: #7f9c45 !important;
  color: white;
  border: none;
  border-radius: 5px;
  cursor: pointer;
  margin-top: 0 !important;

  @media (max-width: 50px) {
    padding: 8px 10px;
    font-size: 0.8em;
  }
`;

const ExitButton = styled.button`
  background: none;
  border: none;
  cursor: pointer;
  padding: 5px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: background-color 0.2s;

  &:hover {
    background-color: #f2f2f2;
  }

  svg {
    color: #333; // Icon color
    width: 20px;
    height: 20px;
  }
`;

const TypingIndicator = styled.div`
  height: 10px;
  width: 10px;
  border-radius: 50%;
  background-color: #999;
  margin: 0 2px;
  animation: blink 1.4s infinite both;

  &:nth-child(2) {
    animation-delay: 0.2s;
  }

  &:nth-child(3) {
    animation-delay: 0.4s;
  }

  @keyframes blink {
    0%, 100% { opacity: 0.2; }
    50% { opacity: 1; }
  }
`;

const API_BASE_URL = process.env.REACT_APP_API_URL || '';

const ChatContainer = () => {
  const [messages, setMessages] = useState([]);
  const [inputMessage, setInputMessage] = useState('');
  const [visibilitystate, setVisibilityState] = useState("false");
  const [isLoading, setIsLoading] = useState(false);
  const messageEndRef = useRef(null);
  const [sessionId] = useState(uuidv4()); // Generate a unique session ID

  useEffect(() => {
    const messageContainer = messageEndRef.current?.parentNode;
    if (messageContainer) {
      messageContainer.scrollTop = messageContainer.scrollHeight;
    }
  }, [messages]);

  const handleChange = (event) => {
    setInputMessage(event.target.value);
  };

  const logMessage = async (role, content) => {
    try {
      await axios.post(`${API_BASE_URL}/api/forms/chat/log/`, {
        role,
        content,
        session_id: sessionId
      });
    } catch (error) {
      console.error('Failed to log chat message:', error);
    }
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    const userMessage = inputMessage;
    setInputMessage('');

    setMessages(prevMessages => [...prevMessages, { role: "user", content: userMessage }]);
    setIsLoading(true);

    // Log user message
    await logMessage('user', userMessage);

    try {
      const response = await axios.post('https://iscreenfix.com/api/generate/', {
        messages: messages.map(message => ({
          role: message.role,
          content: message.content,
        })).concat([{ role: "user", content: userMessage }])
      },
        {
          headers: {
            Authorization: `Token ${localStorage.getItem('accessToken')}`
          }
        });

      const assistantMessageContent = response.data.message.content;

      setMessages(prevMessages => [
        ...prevMessages,
        { role: "assistant", content: assistantMessageContent }
      ]);

      // Log AI response
      await logMessage('assistant', assistantMessageContent);
    } catch (error) {
      console.error("Failed to fetch response from backend:", error);
      if (error.response && error.response.status === 401) {
        setMessages(prevMessages => [
          ...prevMessages,
          { role: "assistant", content: "Please sign in to use the chat feature." }
        ]);
      } else {
        setMessages(prevMessages => [
          ...prevMessages,
          { role: "assistant", content: "Sorry, I couldn't fetch a response." }
        ]);
      }
    } finally {
      setIsLoading(false);
    }
  };

  const toggleChatVisibility = () => {
    setVisibilityState(visibilitystate === "false" ? "true" : "false");
  };

  return (
    <>
      <ToggleButton onClick={toggleChatVisibility}>
        <FontAwesomeIcon icon={visibilitystate === "true" ? faTimes : faComment} />
      </ToggleButton>
      <ChatBox $visibilitystate={visibilitystate}>
        <ChatHeader>
          <AltLogo src="/assets/images/ico.png" />
          <h2>Chat with AI</h2>
          <ExitButton onClick={toggleChatVisibility}>
            <FontAwesomeIcon icon={faTimes} />
          </ExitButton>
        </ChatHeader>

        <MessageContainer>
          {messages.map((message, index) => (
            <Message key={index} type={message.role}>
              {message.content}
            </Message>
          ))}
          {isLoading && (
            <div style={{ display: 'flex', justifyContent: 'center', margin: '10px 0' }}>
              <TypingIndicator />
              <TypingIndicator />
              <TypingIndicator />
            </div>
          )}
          <div ref={messageEndRef} />
        </MessageContainer>

        <ChatForm onSubmit={handleSubmit}>
          <Input
            type="text"
            name="inputMessage"
            value={inputMessage}
            onChange={handleChange}
            placeholder="Type your message..."
          />
          <SendButton type="submit">Send</SendButton>
        </ChatForm>
      </ChatBox>
    </>
  );
};

export default ChatContainer;
